/**
 * CombineSimilarDialog — Two-step combine flow with similarity suggestions.
 *
 * Step 1: Ask "Ähnlich verbinden?" (Ja/Nein)
 * Step 2 (if Ja): Show list of structurally similar token groups that can be
 * combined using the same pattern as the source tokens.
 */
import { useState, useMemo, useEffect } from 'react'
import { Button } from '@/components/common/Button'
import { Input } from '@/components/common/Input'
import {
  extractTokenSignature,
  calculateAdaptiveSimilarity,
  calculateStructuralSimilarity,
} from '@/utils/batchEdit'

interface CombineSuggestion {
  id: string
  combinedText: string
  sourceTokens: string[]
  confidence: number
}

interface CombineSimilarDialogProps {
  isOpen: boolean
  sourceTokens: string[]
  allUnmatchedTokens: string[]
  allMatchedTokens: string[]
  onCombineWithSimilar: (sourceTokens: string[], similarSuffixes: string[], field?: string | null) => void
  onSubjectResolutionNeeded?: (combinedToken: string) => void
  onCancel: () => void
  threshold?: number
}

const FIELD_LABELS: Record<string, string> = {
  teacher_code: 'Lehrer-Code',
  teacher_name: 'Lehrer-Name',
  first_name: 'Vorname',
  family_name: 'Nachname',
  subject: 'Fach',
  room: 'Raum',
  class_name: 'Klasse',
  time_slot: 'Zeit',
  ignore: 'Ignorieren',
}

export function CombineSimilarDialog({
  isOpen,
  sourceTokens,
  allUnmatchedTokens,
  allMatchedTokens,
  onCombineWithSimilar,
  onSubjectResolutionNeeded,
  onCancel,
  threshold = 0.5,
}: CombineSimilarDialogProps) {
  const [step, setStep] = useState<'ask' | 'suggestions' | 'simple'>('ask')
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set())
  const [simpleText, setSimpleText] = useState('')
  const [simpleField, setSimpleField] = useState('')

  // Reset state when dialog opens with new tokens
  useEffect(() => {
    if (isOpen) {
      setStep('ask')
      setSelectedSuggestions(new Set())
      setSimpleText(sourceTokens.join(' '))
      setSimpleField('')
    }
  }, [isOpen, sourceTokens])

  // Generate similarity-based suggestions
  const suggestions = useMemo((): CombineSuggestion[] => {
    if (sourceTokens.length < 2) return []

    const allTokens = [...allUnmatchedTokens, ...allMatchedTokens]

    // Find tokens similar to each source token
    const similarGroups: Map<string, string[]> = new Map()

    for (const srcToken of sourceTokens) {
      const sig = extractTokenSignature(srcToken)
      const similar: string[] = []

      for (const t of allTokens) {
        if (sourceTokens.includes(t)) continue
        const adaptiveSim = calculateAdaptiveSimilarity(sig, t)
        const structSim = calculateStructuralSimilarity(srcToken, t)
        const similarity = Math.max(adaptiveSim, structSim)
        if (similarity >= threshold) {
          similar.push(t)
        }
      }

      if (similar.length > 0) {
        similarGroups.set(srcToken, similar)
      }
    }

    // Generate combination suggestions
    const results: CombineSuggestion[] = []

    // Suggestion 1: Combine source tokens directly
    const directCombined = sourceTokens.join(' ')
    results.push({
      id: 'direct',
      combinedText: directCombined,
      sourceTokens: [...sourceTokens],
      confidence: 1.0,
    })

    // Suggestion 2: Dash-joined version
    if (sourceTokens.length === 2) {
      const dashCombined = `${sourceTokens[0]}-${sourceTokens[1]}`
      results.push({
        id: 'dash',
        combinedText: dashCombined,
        sourceTokens: [...sourceTokens],
        confidence: 0.8,
      })
    }

    // Suggestion 3: For each similar group, create combinations
    for (const [srcToken, similar] of similarGroups) {
      for (const simToken of similar.slice(0, 3)) {
        const combined = `${srcToken} ${simToken}`
        const id = `similar_${srcToken}_${simToken}`
        results.push({
          id,
          combinedText: combined,
          sourceTokens: [srcToken, simToken],
          confidence: 0.7,
        })
      }
    }

    return results.slice(0, 15) // Limit to top 15
  }, [sourceTokens, allUnmatchedTokens, allMatchedTokens, threshold])

  const toggleSuggestion = (id: string) => {
    setSelectedSuggestions(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAllSuggestions = () => {
    setSelectedSuggestions(new Set(suggestions.map(s => s.id)))
  }

  const handleApplySelected = () => {
    const selected = suggestions.filter(s => selectedSuggestions.has(s.id))
    if (selected.length === 0) return

    // Extract suffixes from selected suggestions (the token that differs from the prefix)
    const prefix = sourceTokens[0]
    const initialSuffix = sourceTokens[1]
    const similarSuffixes = selected.map(s => {
      const otherToken = s.sourceTokens.find(t => t.toLowerCase() !== prefix?.toLowerCase())
      return otherToken || s.sourceTokens[1]
    }).filter((s): s is string => s !== undefined && s.toLowerCase() !== initialSuffix?.toLowerCase())

    onCombineWithSimilar(sourceTokens, similarSuffixes)
    onCancel()
  }

  const handleSimpleApply = () => {
    if (!simpleText.trim()) return
    
    // If subject is selected, use special callback for resolution
    if (simpleField === 'subject' && onSubjectResolutionNeeded) {
      onSubjectResolutionNeeded(simpleText.trim())
      onCancel()
      return
    }
    
    onCombineWithSimilar(sourceTokens, [], simpleField || null)
    onCancel()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onCancel}>
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        {step === 'ask' && (
          <>
            <h3 className="font-medium text-lg mb-2">Token verbinden</h3>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">Ausgewählte Token:</p>
              <div className="flex flex-wrap gap-1 mb-3">
                {sourceTokens.map(t => (
                  <code key={t} className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono">{t}</code>
                ))}
              </div>
              <p className="text-sm font-medium text-gray-700">Ähnlich verbinden?</p>
              <p className="text-xs text-gray-500 mt-1">
                Wenn ja, werden strukturell ähnliche Token gesucht und als Vorschläge angezeigt.
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="primary" className="flex-1" onClick={() => setStep('suggestions')}>
                Ja, ähnlich verbinden
              </Button>
              <Button variant="secondary" className="flex-1" onClick={() => setStep('simple')}>
                Nein, einfach verbinden
              </Button>
            </div>
            <div className="mt-2">
              <Button variant="secondary" className="w-full" onClick={onCancel}>
                Abbrechen
              </Button>
            </div>
          </>
        )}

        {step === 'suggestions' && (
          <>
            <h3 className="font-medium text-lg mb-2">Ähnliche Verbindungen</h3>
            <div className="mb-3 max-h-64 overflow-y-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">{suggestions.length} Vorschläge</span>
                <button
                  className="text-xs text-blue-600 hover:underline"
                  onClick={selectAllSuggestions}
                >
                  Alle auswählen
                </button>
              </div>
              {suggestions.map(s => (
                <div
                  key={s.id}
                  className={`flex items-center gap-2 p-2 mb-1 rounded cursor-pointer ${
                    selectedSuggestions.has(s.id) ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-transparent'
                  }`}
                  onClick={() => toggleSuggestion(s.id)}
                >
                  <input
                    type="checkbox"
                    checked={selectedSuggestions.has(s.id)}
                    onChange={() => toggleSuggestion(s.id)}
                    className="rounded"
                  />
                  <code className="text-xs font-mono flex-1">{s.combinedText}</code>
                  <span className="text-xs text-gray-500">{Math.round(s.confidence * 100)}%</span>
                </div>
              ))}
              {suggestions.length === 0 && (
                <div className="text-center py-4 text-gray-500 text-sm">
                  Keine ähnlichen Token gefunden.
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="primary"
                className="flex-1"
                onClick={handleApplySelected}
                disabled={selectedSuggestions.size === 0}
              >
                Ausgewählte verbinden ({selectedSuggestions.size})
              </Button>
              <Button variant="secondary" onClick={() => setStep('ask')}>
                Zurück
              </Button>
            </div>
          </>
        )}

        {step === 'simple' && (
          <>
            <h3 className="font-medium text-lg mb-2">Token verbinden</h3>
            <div className="mb-3">
              <p className="text-sm text-gray-600 mb-1">{sourceTokens.length} Token:</p>
              <div className="flex flex-wrap gap-1 mb-3">
                {sourceTokens.map(t => (
                  <code key={t} className="text-xs bg-gray-100 px-1.5 py-0.5 rounded font-mono">{t}</code>
                ))}
              </div>
            </div>
            <div className="mb-3">
              <label className="text-sm font-medium mb-1 block">Verbundener Token</label>
              <Input value={simpleText} onChange={e => setSimpleText(e.target.value)} placeholder="Token-Text eingeben..." />
            </div>
            <div className="mb-4">
              <label className="text-sm font-medium mb-1 block">Feldtyp (optional)</label>
              <select
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                value={simpleField}
                onChange={e => setSimpleField(e.target.value)}
              >
                <option value="">— Nicht zugeordnet —</option>
                {Object.entries(FIELD_LABELS).map(([field, label]) => (
                  <option key={field} value={field}>{label}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <Button variant="primary" className="flex-1" onClick={handleSimpleApply} disabled={!simpleText.trim()}>
                Verbinden
              </Button>
              <Button variant="secondary" className="flex-1" onClick={() => setStep('ask')}>
                Zurück
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
